import type { ComponentType } from 'react'
import { BarChart3, CalendarDays, DollarSign, FileText, History, Home, Link2, Map, Navigation, Route, ScrollText, User } from 'lucide-react'
import { ROUTE_PLANNING_ENABLED } from '../../lib/features'

export type AppRole = 'manager' | 'driver'

export type NavItem = {
  to: string
  label: string
  shortLabel: string
  title: string
  icon: ComponentType<{ className?: string }>
  exact?: boolean
}

function LayoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  )
}

const managerNavItems: NavItem[] = [
  { to: '/manager', label: 'Панель управления', shortLabel: 'Панель', title: 'Панель', icon: LayoutIcon, exact: true },
  { to: '/tracking', label: 'Отслеживание водителей', shortLabel: 'Трекинг', title: 'Отслеживание водителей', icon: Navigation, exact: true },
  { to: '/saved-links', label: 'Полученные по ссылке', shortLabel: 'По ссылке', title: 'Полученные по ссылке', icon: Link2 },
  { to: '/manager/history', label: 'История перевозок', shortLabel: 'История', title: 'История', icon: History },
  { to: '/manager/pricing', label: 'Тариф и оплата', shortLabel: 'Тарифы', title: 'Тариф и оплата', icon: DollarSign },
  { to: '/manager/analytics', label: 'Аналитика', shortLabel: 'Аналитика', title: 'Аналитика', icon: BarChart3 },
  // Desktop only and not shipped yet: shown with VITE_ROUTE_PLANNING (see lib/features.ts).
  ...(ROUTE_PLANNING_ENABLED
    ? [{ to: '/manager/route-planning', label: 'Маршрутизация', shortLabel: 'Маршруты', title: 'Маршрутизация', icon: Route }]
    : []),
]

const driverNavItems: NavItem[] = [
  { to: '/driver', label: 'Заявки', shortLabel: 'Заявки', title: 'Заявки', icon: Map, exact: true },
  { to: '/driver/documents', label: 'Фото и документы', shortLabel: 'Фото и документы', title: 'Фото и документы', icon: FileText },
  { to: '/driver/history', label: 'История', shortLabel: 'История', title: 'История', icon: History },
]

const profileItem: NavItem = {
  to: '/profile',
  label: 'Профиль',
  shortLabel: 'Профиль',
  title: 'Профиль',
  icon: User,
  exact: true,
}

const managerMobileNavItems: NavItem[] = [
  { to: '/manager', label: 'Главная', shortLabel: 'Главная', title: 'Главная', icon: Home, exact: true },
  { to: '/manager/orders', label: 'Заявки', shortLabel: 'Заявки', title: 'Заявки', icon: ScrollText },
  { to: '/tracking', label: 'Трекинг', shortLabel: 'Трекинг', title: 'Трекинг', icon: Navigation, exact: true },
  { to: '/manager/calendar', label: 'Календарь', shortLabel: 'Календарь', title: 'Календарь', icon: CalendarDays },
]

const driverMobileNavItems: NavItem[] = [
  { to: '/driver', label: 'Главная', shortLabel: 'Главная', title: 'Главная', icon: Home, exact: true },
  { to: '/driver/trips', label: 'Заявки', shortLabel: 'Заявки', title: 'Заявки', icon: Map },
  { to: '/driver/documents', label: 'Файлы', shortLabel: 'Файлы', title: 'Фото и документы', icon: FileText },
  { to: '/driver/history', label: 'История', shortLabel: 'История', title: 'История', icon: History },
]

export function getNavItems(role: string | null): NavItem[] {
  if (!role) return []

  const roleItems = role === 'manager'
    ? managerNavItems
    : role === 'driver'
      ? driverNavItems
      : []

  return [...roleItems, profileItem]
}

export function getMobileNavItems(role: string | null): NavItem[] {
  if (role === 'manager') return managerMobileNavItems
  if (role === 'driver') return driverMobileNavItems
  return []
}

/**
 * The cabinet a page belongs to, or null for pages both share (profile, saved links, legal…).
 *
 * For an account holding both roles the page on screen has to win over the remembered cabinet:
 * opening /manager from a bookmark or a push while «Водитель» was the last choice showed the
 * manager dashboard inside the driver's menu, with the switcher pointing at the wrong cabinet.
 * The invite (/driver/accept) is left out — it is opened by managers too and decides on its own.
 */
export function cabinetOfPath(pathname: string): AppRole | null {
  if (pathname === '/manager' || pathname.startsWith('/manager/') || pathname === '/tracking') return 'manager'
  if (pathname.startsWith('/driver/accept')) return null
  if (pathname === '/driver' || pathname.startsWith('/driver/')) return 'driver'
  return null
}

export function isNavItemActive(pathname: string, item: NavItem) {
  if (item.exact) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}
